import { useAtomValue } from "jotai";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import { join } from "path";
import { useEffect } from "react";

import { sequentialLoadingState } from "~/atoms/sequential-loading";
import AccessibilityStatement from "~/components/editorials/accessibility-statement/accessibility-statement";
import EditorialEntry from "~/components/editorials/editorial-entry";
import LayoutPage from "~/components/layout/layout-page";
import { usePageTracking } from "~/contexts/page-trackings";
import appRoutes from "~/utils/app-routes";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetStaticPropsType<typeof getStaticProps>;

export default function EntryPreviewPage(props: Props) {
  usePageTracking("editorial", TrackingUtils.mapTrackEditorialPage(props));
  const sequentialLoadingReady = useAtomValue(sequentialLoadingState);

  useEffect(() => {
    const hideDidomiNotice = (unmount?: boolean) => {
      const excludedRoutes = [appRoutes.COOKIE_POLICY, appRoutes.PRIVACY_POLICY];

      if (excludedRoutes.some((r) => props.slugs?.["default"].includes(r))) {
        if (sequentialLoadingReady.didomi) {
          const didomiHost = document.getElementById("didomi-host");
          if (didomiHost) {
            if (unmount) {
              didomiHost.setAttribute("data-visible", "true");
            } else {
              didomiHost.setAttribute("data-visible", "false");
            }
          }
        }
      }
    };

    hideDidomiNotice();

    return () => {
      hideDidomiNotice(true);
    };
  }, [props.slugs, sequentialLoadingReady.didomi]);

  return (
    <LayoutPage {...props}>
      <>
        {props.slugs?.["default"] === appRoutes.ACCESSIBILITY_STATEMENT ? <AccessibilityStatement /> : null}
        {props.page.components?.filter(isActiveEntry).map((entry) => (
          <EditorialEntry key={entry.sys.id} entry={entry} />
        ))}
      </>
    </LayoutPage>
  );
}

export async function getStaticPaths() {
  return {
    paths: [], // TODO: add frequent pages
    fallback: "blocking",
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  //SEO issues: if the slug returned by the editorial pages is "homepage", redirect to the main page
  if (context.params?.editorial?.[0] === process.env.NEXT_PUBLIC_HOMEPAGE_SLUG!) {
    return {
      redirect: {
        destination: join("/", context.locale!),
        permanent: false,
      },
    };
  }

  //SEO issues: if the slug returned by the editorial pages is "untitle-entry", redirect to 404
  if (context.params?.editorial?.[0].includes("untitled-entry")) {
    return {
      notFound: true,
    };
  }

  const pageData = await ssrUtils.getServerSideEditorialPageData(context, context.draftMode, context.params?.editorial);

  if (!pageData.page) {
    if (CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_CONTENTFUL_ENABLE_PREVIEW_FALLBACK)) {
      const previewData = await ssrUtils.getServerSideEditorialPreviewPageData(context, context.params?.editorial);

      if (previewData.page?.fallbackUrl) {
        if (previewData.page.fallbackUrl === "NOT_FOUND") {
          return {
            notFound: true,
            revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
          };
        }

        let destination = join("/", context.locale!, previewData.page.fallbackUrl, "/");

        return {
          redirect: {
            destination,
            permanent: false,
          },
        };
      }
    }

    //get redirects from json file
    const redirects = await ssrUtils.getRedirects(context.locale);

    if (redirects) {
      const paramsEditorial = context.params?.editorial;

      if (paramsEditorial && typeof paramsEditorial !== "string") {
        const relativeUrl = "/" + context.locale + "/" + (paramsEditorial ? paramsEditorial.join("/") + "/" : "");
        const destination = redirects[relativeUrl];

        if (destination) {
          return {
            redirect: {
              destination,
              permanent: true,
            },
            revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
          };
        }
      }
    }

    // The page could not be found in draft mode, did not have a fallback URL, and no redirect was available.

    return {
      notFound: true,
      revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
    };
  }

  if (pageData.page.fallbackUrl) {
    console.warn(`Detected fallback url for editorial page ${context.params?.editorial}: ${pageData.page.fallbackUrl}`);

    if (pageData.page.fallbackUrl === "NOT_FOUND") {
      return {
        notFound: true,
        revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
      };
    }

    let destination = join("/", context.locale!, pageData.page.fallbackUrl, "/");

    return {
      redirect: {
        destination,
        permanent: true,
      },
    };
  }

  return {
    props: {
      ...pageData,
    },
    revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
  };
}
